import {
  atom,
  Resetter,
  SetterOrUpdater,
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from 'recoil';
import isString from 'lodash/isString';

import * as K from 'common/constants';
import log from 'common/ScalyrLogger';
import { parseTimeRange } from 'common/util/time';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';

/**
 * Types
 */

type TimeRangeTime = {
  query: string; // time used in request to server
  display: string; // time shown to user
};

export type TimeRangeDropdownState = {
  showOverlay: boolean;
  startTime: TimeRangeTime;
  endTime: TimeRangeTime;
  errors: {
    startTime: boolean;
    endTime: boolean;
  };
};

type UseTimeRangeDropdownStateReturnValue = {
  state: TimeRangeDropdownState;
  setState: SetterOrUpdater<TimeRangeDropdownState>;
  resetState: Resetter;
  setShowOverlay: (show: boolean) => void;
  handleInputValidation: () => { status: TimeRangeDropdownValidationStatus };
  setStartTime: (value: TimeRangeTime | string) => void;
  setEndTime: (value: TimeRangeTime | string) => void;
};

export enum TimeRangeDropdownValidationStatus {
  OK = 'ok',
  ERROR = 'error',
}

/**
 * Hooks
 */
export const timeRangeDropdownState = atom<TimeRangeDropdownState>({
  key: K.GLOBAL_KEY.TIME_RANGE_DROPDOWN_STATE,
  default: {
    showOverlay: false,
    startTime: { query: '', display: '' },
    endTime: { query: '', display: '' },
    errors: { startTime: false, endTime: false },
  },
});

export function useTimeRangeDropdownState(): UseTimeRangeDropdownStateReturnValue {
  /**
   * Dependencies
   */
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);

  /**
   * State
   */
  const [state, setState] = useRecoilState(timeRangeDropdownState);
  const resetState = useResetRecoilState(timeRangeDropdownState);
  const timezone = useRecoilValue(timezoneState);

  /**
   * Actions
   */
  const setShowOverlay = (show: boolean): void => {
    setState((currentState) => ({
      ...currentState,
      showOverlay: show,
    }));
  };

  const setStartTime = (value: TimeRangeTime | string): void => {
    const startTime = isString(value) ? { query: value, display: value } : value;
    setState((currentState) => ({
      ...currentState,
      startTime,
    }));
  };

  const setEndTime = (value: TimeRangeTime | string): void => {
    const endTime = isString(value) ? { query: value, display: value } : value;
    setState((currentState) => ({
      ...currentState,
      endTime,
    }));
  };

  const handleInputValidation = (): { status: TimeRangeDropdownValidationStatus } => {
    const startTime = state.startTime.query;
    const endTime = state.endTime.query;
    const { startTime: parsedStartTime, endTime: parsedEndTime } = parseTimeRange({ startTime, endTime }, timezone);

    log.debug('Validating input: ', { startTime, endTime });
    let errorMessage = '';
    const timeRangeErr = { startTime: false, endTime: false };

    if (startTime !== '' && parsedStartTime === null) {
      log.debug('Invalid startTime: ', `[${startTime}]`);
      timeRangeErr.startTime = true;
    }
    if (endTime !== '' && String(endTime).toUpperCase() !== K.DEFAULT_SEARCH_END && parsedEndTime === null) {
      log.debug('Invalid endTime: ', `[${endTime}]`);
      timeRangeErr.endTime = true;
    }
    if (
      parsedStartTime !== null &&
      parsedEndTime !== null &&
      parsedEndTime.moment.isSameOrBefore(parsedStartTime.moment)
    ) {
      log.debug('Invalid endTime: ', 'End time must be after start time');
      timeRangeErr.endTime = true;
      errorMessage = 'End time must be after start time';
    }

    if (timeRangeErr.startTime || timeRangeErr.endTime) {
      // Update the state
      setState((currentState) => ({
        ...currentState,
        showOverlay: true,
        errors: timeRangeErr,
      }));

      // Display an alert with error
      if (errorMessage === '') {
        errorMessage = `Invalid time: ${timeRangeErr.startTime ? `[${startTime}]` : ''} ${
          timeRangeErr.endTime ? `[${endTime}]` : ''
        }`;
      }
      setPageAlertConfig({ message: errorMessage, variant: PageAlertVariant.ERROR, autoHide: true });

      return { status: TimeRangeDropdownValidationStatus.ERROR };
    }

    // No errors found – clear error state
    setState((currentState) => ({
      ...currentState,
      showOverlay: false,
      errors: timeRangeErr,
    }));

    return { status: TimeRangeDropdownValidationStatus.OK };
  };

  return {
    state,
    setState,
    resetState,
    setShowOverlay,
    handleInputValidation,
    setStartTime,
    setEndTime,
  };
}
